import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useDateRange } from "@/context/DateRangeContext";
import { useHeaderStateProvider } from "@/context/HeaderStateContext";
import {
  portfolioCampaignsService,
  type PortfolioCampaignsRequest,
  type PortfolioCampaignsResponse,
} from "../services/portfolioCampaignsService";

const formatDate = (d: Date) => dayjs(d).format("YYYY-MM-DD");

export const PORTFOLIO_CAMPAIGNS_QUERY_KEYS = {
  campaigns: "portfolio-campaigns",
};

export interface UsePortfolioCampaignsResult {
  campaigns: PortfolioCampaignsResponse["data"];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
}

export function usePortfolioCampaigns(
  portfolioIds: string[],
  pageSize: number = 10,
  pageNumber: number = 1
): UsePortfolioCampaignsResult {
  const { dateRange } = useDateRange();
  const { adAccounts } = useHeaderStateProvider();
  const location = useLocation();
  const isAdsRoute = location.pathname.toLowerCase().includes("/ads");

  // Track localStorage changes for ads profile selection, mirroring usePortfolioMetrics
  const [localStorageVersion, setLocalStorageVersion] = useState(0);

  useEffect(() => {
    if (!isAdsRoute) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "app_v2_ads_profiles") {
        setLocalStorageVersion((prev) => prev + 1);
      }
    };

    const handleAdsProfileChange = () => {
      setLocalStorageVersion((prev) => prev + 1);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("adsProfileChanged", handleAdsProfileChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adsProfileChanged", handleAdsProfileChange);
    };
  }, [isAdsRoute]);

  // Track localStorage changes for ads profile selection
  const profileIds = useMemo(() => {
    if (isAdsRoute) {
      try {
        const stored = localStorage.getItem("app_v2_ads_profiles");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed as string[];
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to parse stored ads profiles:", error);
      }

      const allIds = adAccounts
        .flatMap((account) => account.profiles)
        .map((profile) => profile.profileId);
      return allIds.length > 0 ? [allIds[0]] : [];
    } else {
      return adAccounts
        .flatMap((account) => account.profiles)
        .filter((profile) => profile.selected)
        .map((profile) => profile.profileId);
    }
  }, [adAccounts, isAdsRoute, localStorageVersion]);

  const requestBody: PortfolioCampaignsRequest = useMemo(
    () => ({
      startDate: formatDate(dateRange.from),
      endDate: formatDate(dateRange.to),
      profileIds,
      portfolioIds,
      pageSize,
      pageNumber,
    }),
    [dateRange, profileIds, portfolioIds, pageSize, pageNumber]
  );

  const enabled = profileIds.length > 0 && portfolioIds.length > 0;

  const query = useQuery<PortfolioCampaignsResponse, Error>({
    queryKey: [
      PORTFOLIO_CAMPAIGNS_QUERY_KEYS.campaigns,
      requestBody.startDate,
      requestBody.endDate,
      requestBody.profileIds.join(","),
      requestBody.portfolioIds.join(","),
      requestBody.pageSize,
      requestBody.pageNumber,
      isAdsRoute ? "ads" : "non-ads",
    ],
    queryFn: () => portfolioCampaignsService.fetchCampaigns(requestBody),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Format decimal values to 2 decimal places (for percentages and currency)
  // Filter out "All Campaigns" summary row
  const formattedCampaigns = useMemo(() => {
    if (!query.data?.data) return [];
    
    // Filter out the "All Campaigns" summary row
    const filteredData = query.data.data.filter(
      (campaign) => campaign.campaign_name !== "All Campaigns"
    );
    
    return filteredData.map((campaign) => {
      // Helper function to safely format numbers
      const formatNumber = (value: number | null | undefined): number => {
        if (value == null || typeof value !== 'number' || isNaN(value)) {
          return 0;
        }
        return Number(value.toFixed(2));
      };
      
      return {
        ...campaign,
        // Keep integers as integers (impressions, clicks, orders)
        impressions: campaign.impressions ?? 0,
        clicks: campaign.clicks ?? 0,
        ad_orders: campaign.ad_orders ?? 0,
        // Format currency and percentages to 2 decimal places
        ad_revenue: formatNumber(campaign.ad_revenue),
        spend: formatNumber(campaign.spend),
        ctr: formatNumber(campaign.ctr),
        ad_acos: formatNumber(campaign.ad_acos),
        cr: formatNumber(campaign.cr),
        ad_roas: campaign.ad_roas != null && typeof campaign.ad_roas === 'number' && !isNaN(campaign.ad_roas)
          ? Number(campaign.ad_roas.toFixed(2))
          : null,
      };
    });
  }, [query.data?.data]);

  // Adjust total count to exclude "All Campaigns" summary row
  const adjustedTotalCount = useMemo(() => {
    if (!query.data?.data) return 0;
    const hasSummaryRow = query.data.data.some(
      (campaign) => campaign.campaign_name === "All Campaigns"
    );
    return hasSummaryRow 
      ? Math.max(0, (query.data.total_count ?? 0) - 1)
      : (query.data.total_count ?? 0);
  }, [query.data?.data, query.data?.total_count]);

  return {
    campaigns: formattedCampaigns,
    totalCount: adjustedTotalCount,
    isLoading: query.isLoading,
    error: query.error ?? null,
  };
}
